import React, {Key, ReactNode} from 'react';
import styled from 'styled-components';

import {fontSizes, fontWeights} from '../../styles/fontStyles';
import {RadioButton} from './radioButton';

/*
 * Props.
 */

export interface RadioPickerOption<T extends Key> {
  id: T;
  title: ReactNode;
  description?: ReactNode;
}

interface RadioPickerProps<T extends Key> {
  options: ReadonlyArray<RadioPickerOption<T>>;
  onSelect: (id: T) => void;
  selectedOptionId?: T;
  isDisabled?: boolean;
  className?: string;
}

/*
 * Style.
 */

const StyledRadioButtonContent = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 10px;
  row-gap: 6px;
`;
const StyledOptionTitle = styled.div`
  font-size: ${fontSizes.large};
  font-weight: ${fontWeights.medium};
  color: ${(p) => p.theme.greys.shade80};
`;
const StyledOptionSubtitle = styled.div`
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.normal};
  color: ${(p) => p.theme.greys.shade70};
`;
const StyledDiv = styled.div`
  display: grid;
  grid-auto-flow: row;
  grid-auto-rows: min-content;
  grid-row-gap: 16px;
`;

/*
 * Component.
 */

export const RadioPicker = <T extends Key>(props: RadioPickerProps<T>) => (
  <StyledDiv className={props.className}>
    {props.options.map((option) => (
      <RadioButton
        key={option.id}
        isChecked={props.selectedOptionId === option.id}
        onChange={() => {
          props.onSelect(option.id);
        }}
        isDisabled={props.isDisabled}
      >
        <StyledRadioButtonContent>
          <StyledOptionTitle>{option.title}</StyledOptionTitle>
          {(option.description && <StyledOptionSubtitle>{option.description}</StyledOptionSubtitle>) || null}
        </StyledRadioButtonContent>
      </RadioButton>
    ))}
  </StyledDiv>
);
